import { useState, useEffect } from "react";
import { useUser } from "@/lib/clerkShim";
import { api } from "@/lib/apiClient";

export type UserRole = 'seller' | 'buyer' | 'internal_admin' | 'internal_reviewer' | 'admin' | 'member' | 'ceo' | null;

interface UseUserRoleResult {
  roles: UserRole[];
  primaryRole: UserRole;
  loading: boolean;
  hasRole: (role: UserRole) => boolean;
  isInternal: boolean;
  isAdmin: boolean;
  isSeller: boolean;
  isBuyer: boolean;
  isCeo: boolean;
}

export const useUserRole = (): UseUserRoleResult => {
  const { user, isLoaded } = useUser();
  const [apiRoles, setApiRoles] = useState<UserRole[] | null>(null);
  const [apiLoading, setApiLoading] = useState(false);

  // Roles from Clerk publicMetadata (synced via webhook)
  const clerkRoles: UserRole[] = isLoaded && user?.publicMetadata?.roles
    ? (user.publicMetadata.roles as UserRole[])
    : [];

  const userId = user?.id;

  // Fallback: fetch roles from API (which reads from DB) when Clerk metadata is empty
  useEffect(() => {
    if (!isLoaded || !userId) return;
    if (clerkRoles.length > 0) return; // Clerk has roles, no need for API
    if (apiRoles !== null) return; // Already fetched

    setApiLoading(true);
    api.get<string[]>(`/users/${userId}/roles`)
      .then((roles) => {
        setApiRoles(roles as UserRole[]);
      })
      .catch(() => {
        setApiRoles([]); // Fallback to empty on error
      })
      .finally(() => {
        setApiLoading(false);
      });
  }, [isLoaded, userId, clerkRoles.length, apiRoles]);

  const roles: UserRole[] = clerkRoles.length > 0 ? clerkRoles : (apiRoles || []);
  // Must also be "loading" when we know an API fetch is needed but hasn't started yet.
  // Without this, the first render has loading=false + roles=[] which causes
  // ProtectedRoute to redirect before the useEffect fires to start the fetch.
  const needsApiFetch = isLoaded && !!userId && clerkRoles.length === 0 && apiRoles === null;
  const loading = !isLoaded || apiLoading || needsApiFetch;

  const hasRole = (role: UserRole): boolean => {
    if (!role) return false;
    return roles.includes(role);
  };

  const isInternal = roles.includes('internal_admin') || roles.includes('internal_reviewer');
  const isAdmin = roles.includes('internal_admin') || roles.includes('admin');
  const isSeller = roles.includes('seller');
  const isBuyer = roles.includes('buyer');
  const isCeo = roles.includes('ceo');

  const primaryRole: UserRole =
    roles.includes('internal_admin') ? 'internal_admin' :
    roles.includes('internal_reviewer') ? 'internal_reviewer' :
    roles.includes('ceo') ? 'ceo' :
    roles.includes('seller') ? 'seller' :
    roles.includes('buyer') ? 'buyer' :
    roles[0] || null;

  return {
    roles,
    primaryRole,
    loading,
    hasRole,
    isInternal,
    isAdmin,
    isSeller,
    isBuyer,
    isCeo,
  };
};
